import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks'
import { isFormDirty } from '../utils/functions'
import ScreenshotFormHero1 from '../assets/form.jpeg'
import ScreenshotFormHero2 from '../assets/form2.jpeg'
import ScreenshotNewForm from '../assets/newform.jpeg'
import ScreenshotGraph from '../assets/graph.jpeg'
import ScreenshotGraph2 from '../assets/graph2.jpeg'
import faqsData from '../data/faqs.json'

export const LandingView = () => {
  const [email, setEmail] = useState('')
  const [faqs, setFaqs] = useState(faqsData)
  const [isHovering, setIsHovering] = useState(false)

  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const isUserLoggedIn = !!authUser

  const redirectUser = () => navigate(authUser ? '/dashboard' : '/log-in', { viewTransition: true })

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    navigate(`/sign-up?addr=${email}`, { viewTransition: true })
  }

  const updateFaqs = (index: number) => {
    const updatedFaqs = [...faqs]
    const faq = updatedFaqs[index]
    faq.expanded = !faq.expanded
    setFaqs(updatedFaqs)
  }

  return (
    <div className='landing'>
      <section className='landing_banner'>
        <div className='landing_banner_header'>
          <h2>Collect better data with better forms!</h2>
          <h3>Build beautiful forms in minutes and watch your data come to life.</h3>
        </div>
        <div className='landing_banner_cta'>
          {isUserLoggedIn ? (
            <>
              <span>You're already logged in :&#41;</span>
              <button className='button-with-icon' onClick={redirectUser}>
                <span>Go to dashboard</span>
                <span className='material-symbols-rounded'>arrow_right_alt</span>
              </button>
            </>
          ) : (
            <>
              <button onClick={() => navigate('/sign-up', { viewTransition: true })}>
                Create Free Account
              </button>
              <span>
                Already have an account?{' '}
                <em className='span-a' onClick={redirectUser}>
                  Click here to log in
                </em>
              </span>
            </>
          )}
        </div>
        <div className='landing_banner_supporting'>
          <p>
            <span>Your Forms, your Data, your Insights.</span>
            <span>
              Don't just collect data, <em>understand it</em>. Every response is one part of a
              story. What is your data trying to tell you?
            </span>
          </p>
          <div>
            <img src={ScreenshotFormHero2} alt='Expense tracker' />
            <img src={ScreenshotFormHero1} alt='Event registration' />
          </div>
        </div>
      </section>
      <div className='section-banner'>
        <div>
          <h4 className='heading-styled'>Powerful forms for all your needs.</h4>
          <span>Your next form starts here.</span>
        </div>
        <button
          className='button-with-icon cta'
          onClick={() => navigate(`/templates`, { viewTransition: true })}>
          <span>Explore templates</span>
          <span className='material-symbols-rounded'>arrow_right_alt</span>
        </button>
      </div>
      <section className='landing_section'>
        <div onClick={() => navigate(`/templates?filter=public`, { viewTransition: true })}>
          <div className='heading-styled-div'>
            <h3 className='heading-styled'>Public forms</h3>
            <span className='material-symbols-rounded'>arrow_outward</span>
          </div>
          <div className='landing_section-card'>
            <div>
              <span className='material-symbols-rounded'>groups</span>
              <h4>Know your people</h4>
            </div>
            <p>Conduct research, reviews, evaluations, applications and more using our surveys.</p>
          </div>
        </div>
        <div onClick={() => navigate(`/templates?filter=private`, { viewTransition: true })}>
          <div className='heading-styled-div'>
            <h3 className='heading-styled'>Private forms</h3>
            <span className='material-symbols-rounded'>arrow_outward</span>
          </div>
          <div className='landing_section-card'>
            <div>
              <span className='material-symbols-rounded'>ar_on_you</span>
              <h4>Know your self (and your business)</h4>
            </div>
            <p>
              Forms by you, for you. Suitable for anything from business operations to personal
              habit tracking.
            </p>
          </div>
        </div>
      </section>
      <section className='landing_grid'>
        <div>
          <img src={ScreenshotNewForm} alt='New form' />
        </div>
        <div>
          <div>
            <span className='material-symbols-rounded'>add_notes</span>
            <h4>Create a form</h4>
          </div>
          <p>
            Getting valuable insights begins with collecting the right data.{' '}
            <em>Ask the right questions</em> and build up your dataset.
          </p>
        </div>
        <div>
          <div>
            <span className='material-symbols-rounded'>bar_chart</span>
            <h4>Visualize your data</h4>
          </div>
          <p>
            <em>See the story of your data</em> across a range of graphs. Delve into your dataset
            and identify what affects what - and how.
          </p>
        </div>
        <div>
          <img src={ScreenshotGraph} alt='Graph' />
        </div>
        <div>
          <img src={ScreenshotGraph2} alt='Graph' />
        </div>
        <div>
          <div>
            <span className='material-symbols-rounded material-symbols-sm'>palette</span>
            <h4>Customize your experience</h4>
          </div>
          <p>
            Get creative with <em>beautiful themes</em> curated for fun and professional expression.
          </p>
        </div>
      </section>
      <div className='section-banner section-banner-alt'>
        <div>
          <h4 className='heading-styled'>What gets measured, gets managed.</h4>
          <span>- Peter Drucker</span>
        </div>
        <button
          className='button-with-icon cta'
          onClick={() =>
            navigate(`/form/new/${isFormDirty() ? 'edit' : 'select'}`, {
              viewTransition: true
            })
          }>
          <span>{isFormDirty() ? 'Continue with draft' : 'Get started'}</span>
          <span className='material-symbols-rounded'>arrow_right_alt</span>
        </button>
      </div>
      <section className='expandable_list'>
        <h3 className='heading-styled'>FAQs</h3>
        <ul>
          {faqs.map((faq, index) => (
            <li className={`${faq.expanded ? 'expandable_list-open' : ''} `} key={index}>
              <h4>{index + 1}.</h4>
              <div onClick={() => updateFaqs(index)}>
                <h4>{faq.question}</h4>
                <span className='material-symbols-rounded'>
                  {faq.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </span>
              </div>
              {faq.expanded && (
                <>
                  <span />
                  <span>{faq.answer}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
      {!isUserLoggedIn && (
        <div className='cta-join'>
          <h4>Sign up for the public beta!</h4>
          <p>
            Be part of the <em>Springforms community</em> and help contribute to its development.
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type='email'
              value={email}
              onChange={handleInput}
              placeholder='Enter email address...'
              required
            />
            <button
              className='button-with-icon'
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}>
              <span>Let's do it</span>
              <span className='material-symbols-rounded'>
                {isHovering ? 'sentiment_excited' : 'sentiment_very_satisfied'}
              </span>
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
